import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid 
} from "recharts";
import { Package, Users, AlertCircle, TrendingDown, RefreshCw, Headphones, CheckCircle, Clock, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#f97316", "#3b82f6", "#22c55e", "#a855f7", "#ec4899", "#14b8a6", "#eab308", "#6366f1"];

const TIME_PERIODS = [
  { label: "7 Days", value: "7" },
  { label: "30 Days", value: "30" },
  { label: "90 Days", value: "90" },
  { label: "1 Year", value: "365" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [staffItems, setStaffItems] = useState([]);
  const [afterSalesStats, setAfterSalesStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("30");
  const [discrepancySummary, setDiscrepancySummary] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchStaffItems();
    fetchAfterSalesStats();
    fetchDiscrepancySummary();
  }, [timePeriod]);

  const fetchDiscrepancySummary = async () => {
    try {
      const response = await axios.get(`${API}/stock-discrepancies/summary`);
      setDiscrepancySummary(response.data);
    } catch (error) {
      console.error("Error fetching discrepancy summary:", error);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/rts/stats/dashboard?days=${timePeriod}`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffItems = async () => {
    try {
      const response = await axios.get(`${API}/rts/stats/staff-items`);
      setStaffItems(response.data.staff_items || []);
    } catch (error) {
      console.error("Error fetching staff items:", error);
    }
  };

  const fetchAfterSalesStats = async () => {
    try {
      const response = await axios.get(`${API}/after-sales/stats/dashboard`);
      setAfterSalesStats(response.data);
    } catch (error) {
      console.error("Error fetching after-sales stats:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  // Calculate percentages for causes (instead of reasons - mas maikli)
  const totalCauses = stats?.charts?.causes?.reduce((sum, r) => sum + r.value, 0) || 0;
  const causesWithPercent = stats?.charts?.causes?.map(r => ({
    ...r,
    pct: totalCauses > 0 ? Math.round((r.value / totalCauses) * 100) : 0
  })) || [];

  // Custom label renderer for causes pie chart
  const renderCauseLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
      <text 
        x={x} 
        y={y} 
        fill={COLORS[index % COLORS.length]}
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {causesWithPercent[index]?.pct}%
      </text>
    );
  };

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* ANTI-THEFT: Open discrepancy alert banner */}
      {discrepancySummary?.open_count > 0 && (
        <Link to="/admin/stock-audit" data-testid="discrepancy-alert-banner">
          <div className="flex items-center gap-3 p-4 bg-red-50 border-2 border-red-300 rounded-xl hover:bg-red-100 transition-colors cursor-pointer">
            <div className="p-2 bg-red-600 rounded-full animate-pulse">
              <ShieldAlert className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-red-800">
                🚨 {discrepancySummary.open_count} OPEN Stock Discrepancy Alert{discrepancySummary.open_count > 1 ? "s" : ""}!
              </p>
              <p className="text-sm text-red-700">
                May mga stock na nawawala o may variance sa transfers na hindi pa na-e-explain. I-click para makita ang details sa Stock Audit.
              </p>
            </div>
            <Badge className="bg-red-600 text-white text-base px-3 py-1">{discrepancySummary.open_count}</Badge>
          </div>
        </Link>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500">Mga items at staff na may pinakamaraming returns</p>
        </div>
        <Button variant="outline" onClick={fetchStats} size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Time Period Selector */}
      <Card className="border-none shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600 mr-2">Time Period:</span>
            <div className="flex gap-2">
              {TIME_PERIODS.map((period) => (
                <Button
                  key={period.value}
                  variant={timePeriod === period.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTimePeriod(period.value)}
                  className={timePeriod === period.value ? "bg-gray-900 hover:bg-gray-800" : ""}
                  data-testid={`period-${period.value}`}
                >
                  {period.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Returned - Bar Chart */}
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingDown className="w-5 h-5 text-red-500" />
            Mga Items na Bumabalik
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.charts?.items?.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.charts.items.slice(0, 8)} margin={{ bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 11 }} 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Walang data</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Staff Charts - Ordered By and Upselled By SEPARATE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Nag Pa-Order (Ordered By) */}
        <Card className="border-none shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-blue-500" />
              Nag Pa-Order (Ordered By)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.charts?.staff?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.charts.staff}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {stats.charts.staff.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">
                <p>Walang data</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Nag Upsell (Upselled By) */}
        <Card className="border-none shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-green-500" />
              Nag Upsell (Upselled By)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.charts?.upsell_staff?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.charts.upsell_staff}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {stats.charts.upsell_staff.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">
                <p>Walang data</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cause Classification - Pie Chart with List */}
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertCircle className="w-5 h-5 text-purple-500" />
            Cause Classification
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div>
              {causesWithPercent.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={causesWithPercent.slice(0, 6)}
                        cx="50%"
                        cy="50%"
                        innerRadius={0}
                        outerRadius={80}
                        paddingAngle={1}
                        dataKey="value"
                        nameKey="name"
                        label={renderCauseLabel}
                        labelLine={false}
                      >
                        {causesWithPercent.slice(0, 6).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  <p>Walang data</p>
                </div>
              )}
            </div>

            {/* List Breakdown - Compact with scroll */}
            <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
              {causesWithPercent.slice(0, 8).map((cause, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div 
                      className="w-3 h-3 rounded-sm shrink-0" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="font-medium text-gray-700 truncate">{cause.name}</span>
                  </div>
                  <Badge variant="secondary" className="ml-2 shrink-0">{cause.value}</Badge>
                </div>
              ))}
              {causesWithPercent.length > 8 && (
                <p className="text-xs text-gray-400 text-center py-1">
                  +{causesWithPercent.length - 8} more
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-none shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{stats?.summary?.total_today || 0}</p>
            <p className="text-sm text-gray-500">RTS Today</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-orange-600">{stats?.summary?.total_month || 0}</p>
            <p className="text-sm text-gray-500">RTS This Month</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{stats?.summary?.total_all || 0}</p>
            <p className="text-sm text-gray-500">Total RTS</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-xl font-bold text-gray-700 truncate">{stats?.summary?.top_item || "N/A"}</p>
            <p className="text-sm text-gray-500">Top Item</p>
          </CardContent>
        </Card>
      </div>

      {/* Staff Items Breakdown - NEW SECTION */}
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-blue-500" />
            Nag Pa-Order (Staff) - Items na Bumabalik
          </CardTitle>
        </CardHeader>
        <CardContent>
          {staffItems.length > 0 ? (
            <div className="space-y-4">
              {staffItems.map((staff, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="font-semibold text-gray-900">{staff.staff_name}</span>
                    </div>
                    <Badge className="bg-red-100 text-red-800">
                      {staff.total_items} total items
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {staff.items.map((item, itemIndex) => (
                      <div 
                        key={itemIndex} 
                        className="flex items-center justify-between p-2 bg-white rounded border text-sm"
                      >
                        <span className="truncate text-gray-700" title={item.item_name}>
                          {item.item_name}
                        </span>
                        <Badge variant="secondary" className="ml-2 shrink-0">
                          {item.count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Walang data</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* After-Sales Analytics Section */}
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Headphones className="w-5 h-5 text-purple-600" />
            After-Sales Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-gray-700">{afterSalesStats?.summary?.total || 0}</p>
              <p className="text-sm text-gray-500">Total Cases</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-yellow-600">{afterSalesStats?.summary?.pending || 0}</p>
              <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
                <Clock className="w-3 h-3" /> Pending
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-blue-600">{afterSalesStats?.summary?.processing || 0}</p>
              <p className="text-sm text-gray-500">Processing</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-green-600">{afterSalesStats?.summary?.resolved || 0}</p>
              <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
                <CheckCircle className="w-3 h-3" /> Resolved
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Issue Types Chart */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Most Common Issue Types</h4>
              {afterSalesStats?.charts?.issue_types?.length > 0 ? (
                <div className="space-y-2">
                  {afterSalesStats.charts.issue_types.map((issue, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-sm" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm text-gray-700">{issue.name}</span>
                      </div>
                      <Badge variant="secondary">{issue.value}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-4 text-gray-400">Walang data</p>
              )}
            </div>

            {/* Actions Chart */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Actions Taken</h4>
              {afterSalesStats?.charts?.actions?.length > 0 ? (
                <div className="space-y-2">
                  {afterSalesStats.charts.actions.map((action, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-sm" 
                          style={{ backgroundColor: COLORS[(index + 3) % COLORS.length] }}
                        />
                        <span className="text-sm text-gray-700">{action.name}</span>
                      </div>
                      <Badge variant="secondary">{action.value}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-4 text-gray-400">Walang data</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
